import React, { useState } from "react";
import { Link } from "react-router-dom";
import HeroIcon from "../common/HeroIcon";

const PATTERN_TYPE_ICONS = {
  type: "document-text",
  main_topic: "tag",
  content_angle: "light-bulb",
  format: "squares-2x2",
  structure: "queue-list",
  categories: "folder",
  tone: "swatch",
  audience: "users",
  language: "language",
  hook_type: "bolt",
  hook_phrase: "chat-bubble-left-ellipsis",
  hook_visual: "eye",
  cta_type: "cursor-arrow-rays",
  cta_style: "megaphone",
  hashtags: "hashtag",
  channel_tags: "at-symbol",
  text_hook: "bars-3-bottom-left",
  song: "musical-note",
  artist: "user-circle",
};
const getPatternIcon = (t) => PATTERN_TYPE_ICONS[t] || "squares-2x2";

const CHEVRON_DOWN = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const CHEVRON_UP = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const BrandIcon = () => (
  <svg
    className="w-3.5 h-3.5 text-theme-text opacity-70 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.6}
      d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.6}
      d="M6 6h.008v.008H6V6z"
    />
  </svg>
);

const AudienceIcon = () => (
  <svg
    className="w-3.5 h-3.5 text-theme-text opacity-70 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.6}
      d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
    />
  </svg>
);

const tagClassName =
  "inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs border border-theme-secondary bg-theme-secondary text-theme-text opacity-80 hover:opacity-100 hover:bg-theme-main";

const ScriptDetailsAccordion = ({ script }) => {
  const [expanded, setExpanded] = useState(false);
  const description = (script?.script_data?.description ?? script?.idea?.content ?? script?.description ?? "").trim();
  const masterCaption = script?.script_data?.master_caption ?? "";
  const brandName = script?.brand_name;
  const brandHash = script?.brand_hash;
  const audiences = Array.isArray(script?.audiences) ? script.audiences : [];
  const patterns = Array.isArray(script?.patterns) ? script.patterns : [];
  const firstAudience = audiences[0];
  const audienceLabel =
    audiences.length > 0
      ? audiences
          .map((a) => a?.name || "Unnamed audience")
          .filter(Boolean)
          .join(", ")
      : "";
  const hasTags = !!(brandName || audienceLabel || patterns.length);
  const hasAny = !!(description || hasTags || masterCaption);

  if (!hasAny) return null;

  return (
    <div className="mb-3">
      {!expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex w-full items-center justify-between gap-2 px-0 pb-1.5 text-left text-theme-text opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-theme-primary text-sm"
          aria-expanded={false}
          aria-controls="script-details-content"
          id="script-details-toggle"
        >
          {description ? (
            <span className="min-w-0 flex-1 truncate">{description}</span>
          ) : (
            <span className="min-w-0 flex-1">Script details</span>
          )}
          {CHEVRON_DOWN}
        </button>
      ) : (
        <div
          id="script-details-content"
          role="region"
          aria-labelledby="script-details-toggle"
          className="relative space-y-2 pb-2 pr-6 text-sm text-theme-text opacity-80"
        >
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="absolute right-0 top-0 text-theme-text opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-theme-primary"
            aria-expanded={true}
            aria-controls="script-details-content"
            aria-label="Collapse script details"
          >
            {CHEVRON_UP}
          </button>
          {description ? (
            <div>
              <p className="text-xs font-medium opacity-70 mb-0.5">Description</p>
              <p className="whitespace-pre-wrap">{description}</p>
            </div>
          ) : null}
          {masterCaption ? (
            <div>
              <p className="text-xs font-medium opacity-70 mb-0.5">Master caption</p>
              <p className="whitespace-pre-wrap">{masterCaption}</p>
            </div>
          ) : null}
          {hasTags ? (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {brandName ? (
                brandHash ? (
                  <Link to="/brand/profile/" className={tagClassName}>
                    <BrandIcon />
                    <span className="truncate max-w-[180px]">{brandName}</span>
                  </Link>
                ) : (
                  <span className={tagClassName}>
                    <BrandIcon />
                    {brandName}
                  </span>
                )
              ) : null}
              {audienceLabel ? (
                firstAudience?.hash ? (
                  <Link to={`/audience/${firstAudience.hash}/`} className={tagClassName}>
                    <AudienceIcon />
                    <span className="truncate max-w-[180px]">{audienceLabel}</span>
                  </Link>
                ) : (
                  <span className={tagClassName}>
                    <AudienceIcon />
                    {audienceLabel}
                  </span>
                )
              ) : null}
              {patterns.map((p) => {
                const url =
                  p?.audience_hash && p?.pattern_type && p?.pattern_key
                    ? `/audience/${p.audience_hash}/patterns/${encodeURIComponent(p.pattern_type)}/${encodeURIComponent(p.pattern_key)}/`
                    : null;
                const label = p?.display_label || "Pattern";
                const icon = (
                  <HeroIcon
                    name={getPatternIcon(p?.pattern_type)}
                    className="w-3.5 h-3.5 text-theme-text opacity-70 flex-shrink-0"
                  />
                );
                return url ? (
                  <Link
                    key={`${p.audience_hash}-${p.pattern_type}-${p.pattern_key}`}
                    to={url}
                    className={tagClassName}
                  >
                    {icon}
                    <span className="truncate max-w-[180px]">{label}</span>
                  </Link>
                ) : (
                  <span
                    key={`${p.audience_hash}-${p.pattern_type}-${p.pattern_key}`}
                    className={tagClassName}
                  >
                    {icon}
                    <span className="truncate max-w-[180px]">{label}</span>
                  </span>
                );
              })}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ScriptDetailsAccordion;
